<template>
  <BittsModal
    v-if="showReportNotificationsModal"
    variant="data"
    primary-button-text="Save Preferences"
    :title="titleText"
    :loading="initiallyLoading"
    :disabled="!areAllEmailsValid || isLookingUpChannel"
    :use-mask-to-close="true"
    @action="onSave"
    @closed="hide"
  >
    <div ref="form" class="c-notification-settings">
      <div class="c-notification-settings__content">
        <BittsSelectTags
          v-model="emails"
          :form-label="{ title: 'Email Addresses' }"
          class="mb-16"
        />
        <span v-if="!areAllEmailsValid" class="text-danger-text mb-24 text-sm"
          >Must be valid email addresses</span
        >
        <div>
          <div v-if="isSlackEnabled">
            <BittsSelect
              :model-value="selectedChannelName"
              :allow-clear="true"
              :searchable="true"
              :loading="
                isLookingUpChannel || showSyncingState || isFetchingChannels
              "
              :disabled="isLookingUpChannel"
              :dynamic-options="true"
              :form-label="{
                title: 'Slack Channel',
                helpText: `Connecting a Slack channel will notify ${ACCOUNT_OWNER_DISPLAY}s directly through Slack mentions. If you are not syncing your ${ACCOUNT_OWNER_DISPLAY} emails, we will use their first and last name.`,
              }"
              :options="selectableChannels"
              option-type="svg"
              placeholder="Select Channel"
              :class="partnerVisibilityEnabled ? 'mb-8' : 'mb-16'"
              @update:model-value="checkChannelStatus"
              @search-changed="onChannelSearch"
            />
            <SlackChannelSyncAlert />
            <span
              v-if="partnerVisibilityEnabled"
              class="text-sm text-neutral-text-weak mb-16"
            >
              Note: Ensure that everyone in this Slack channel should have
              access to this partner data</span
            >
            <BittsAlert
              v-if="needsReauth"
              :description="reauthDescription"
              message="Your Slack Integration requires an update"
              color="error"
              class="mb-24"
            />
            <BittsAlert
              v-else-if="channelWasDeleted"
              :description="deletedChannelDescription"
              message="We stopped sending Slack notifications to a channel"
              color="error"
              class="mb-24 pt-12"
            >
              <template #link>
                <a
                  href="https://help.crossbeam.com/en/articles/3639783-slack-app"
                  class="border-b-solid border-b-2 border-neutral-400"
                >
                  Learn more
                </a>
              </template>
            </BittsAlert>
            <BittsAlert
              v-if="showCantFindPartnerWarningText"
              :message="getCantFindPartnerWarningText.title"
              :description="getCantFindPartnerWarningText.body"
              color="warning"
              class="mb-24"
            />
            <BittsAlert
              v-if="showSharedWithPartnersText"
              :message="getSharedWithPartnersText.title"
              :description="getSharedWithPartnersText.body"
              color="warning"
              class="mb-24"
            />
          </div>
          <template v-else-if="isSlackUnavailable">
            <BittsAlert
              message="We couldn't load your Slack channels"
              description="Slack is still connected. This is likely a Slack rate limit; try again in a minute."
              color="error"
              class="c-notification-settings__slack-error mb-16"
              data-testid="slack-load-failed-alert"
            >
              <template #button>
                <BittsButton
                  text="Try again"
                  variant="outline"
                  type="neutral"
                  size="small"
                  class="!bg-transparent ml-16 shrink-0"
                  :loading="isLoadingSlack"
                  @click="retryLoadSlack"
                />
              </template>
            </BittsAlert>
            <BittsSelect
              v-if="selectedChannelName"
              :model-value="selectedChannelName"
              :disabled="true"
              :options="slackChannels"
              option-type="svg"
              :form-label="{ title: 'Slack Channel' }"
              class="mb-16"
              data-testid="slack-saved-channel"
            />
          </template>
          <ConnectSlackButton
            v-else-if="isSlackNotConnected"
            :next-url="nextUrl"
          />
        </div>
      </div>
      <span v-if="error" class="c-notification-settings__error">
        {{ error }}
      </span>
    </div>
  </BittsModal>
</template>

<script setup lang="ts">
import {
  BittsAlert,
  BittsButton,
  BittsModal,
  BittsSelect,
  BittsSelectTags,
} from '@crossbeam/bitts';
import { Nullable } from '@crossbeam/types';

import { useVuelidate } from '@vuelidate/core';
import { maxLength } from '@vuelidate/validators';
import { sortBy, uniqBy } from 'lodash';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import ConnectSlackButton from '@/components/ConnectSlackButton.vue';
import SlackChannelSyncAlert from '@/components/SlackChannelSyncAlert.vue';

import { crossbeamApi } from '@/api';
import useSlackChannelSearch from '@/composables/useSlackChannelSearch';
import { TEMP_PARTNER_VISIBILITY } from '@/constants/feature_flags';
import { ACCOUNT_OWNER_DISPLAY } from '@/constants/mdm';
import { PARTNER_GREENFIELD } from '@/constants/reports';
import { captureException } from '@/errors';
import {
  useBillingStore,
  useFeatureFlagStore,
  useFlashesStore,
  usePartnersStore,
  usePopulationsStore,
  useReportsStore,
} from '@/stores';
import {
  Report,
  ReportNotification,
  ReportSlackChannel,
  TrimmedReport,
} from '@/types/reports';
import { focusFirstInvalidField } from '@/utils/focusFirstInvalidField';

type Props = {
  modelValue?: ReportNotification[];
  partnerPopulationIds?: number[];
  reportName?: string;
  showReportNotificationsModal?: boolean;
  fromRoute?: boolean;
  report?: TrimmedReport | Report;
};
const {
  modelValue = [],
  partnerPopulationIds = [],
  reportName = '',
  showReportNotificationsModal = false,
  fromRoute = false,
  report: reportProp = undefined,
} = defineProps<Props>();

const emit = defineEmits<{
  (event: 'update:modelValue', response: ReportNotification[]): void;
  (event: 'notifications-modal-hidden'): void;
}>();

const VALID_SLACK_CHANNEL = /^[a-z\d-_]+$/;
const BEGINS_WITH_HASHTAG = /^#/;
const VALID_EMAIL = /\S+@\S+\.\S+/;

const error = ref<Nullable<string>>(null);
const initiallyLoading = ref(true);
const isLoadingSlack = ref(false);
const saving = ref(false);

const val = computed(() => modelValue);
const rules = {
  modelValue: {
    slack: {
      channel: {
        id: {
          maxLength: maxLength(80),
          validSlackChannel: (id: string) => {
            return (
              !id ||
              (BEGINS_WITH_HASHTAG.test(id) &&
                VALID_SLACK_CHANNEL.test(id.replace('#', ''))) ||
              VALID_SLACK_CHANNEL.test(id)
            );
          },
        },
      },
    },
  },
};
const v$ = useVuelidate(rules, { modelValue: val });
const form = useTemplateRef('form');

const route = useRoute();
const router = useRouter();

const flashesStore = useFlashesStore();
const featureFlagStore = useFeatureFlagStore();
const { isEnterpriseTier } = storeToRefs(useBillingStore());

const { getPartnerOrgById, readySync: partnersReadySync } = usePartnersStore();

const { getPartnerPopulationById } = usePopulationsStore();

const partnerVisibilityEnabled = computed(
  () =>
    featureFlagStore.hasFeatureFlag(TEMP_PARTNER_VISIBILITY) ||
    isEnterpriseTier.value,
);

const reportsStore = useReportsStore();
const { slackApp, hasSlackLoadFailed } = storeToRefs(reportsStore);
const isSlackEnabled = computed(() => !!slackApp.value?.is_enabled);
const isSlackNotConnected = computed(
  () => slackApp.value?.is_enabled === false,
);
const isSlackUnavailable = computed(
  () => hasSlackLoadFailed.value && !slackApp.value,
);

const {
  isChannelListSyncing,
  showSyncingState,
  isFetchingChannels,
  isLookingUpChannel,
  channelSearchQuery,
  cachedChannels,
  customLookupChannels,
  searchChannels,
  onChannelSearch,
  resetSearch,
  lookUpCustomChannel,
} = useSlackChannelSearch();

const reportId = computed<string>(() => {
  return (
    (route?.params?.report_id as string) || (route?.query?.id as string) || ''
  );
});
const report = computed(
  () =>
    reportProp ??
    (reportId.value ? reportsStore.getByReportId(reportId.value) : null),
);

const reportPartnerIdList = computed(() => {
  let partners: number[] = [];
  if (partnerPopulationIds) {
    partners = uniqBy(
      partnerPopulationIds.map(
        (id: number) => getPartnerPopulationById(id)?.organization_id as number,
      ),
      'id',
    );
  }
  return partners;
});

const emails = ref<string[]>([]);
const areAllEmailsValid = computed(() =>
  emails.value.every((email) => isEmailValid(email)),
);
function isEmailValid(email: string) {
  return VALID_EMAIL.test(email.toLowerCase());
}

type SlackChannel = ReportSlackChannel & {
  label: string;
  value: string;
  svg: string;
  isCustom?: boolean;
};
const slackChannels = computed<SlackChannel[]>(() => {
  const channels = [...cachedChannels.value, ...customLookupChannels.value].map(
    (channel: ReportSlackChannel) => {
      return {
        ...channel,
        label: channel.name,
        value: channel.name,
        svg: getSvgName(channel),
      } as SlackChannel;
    },
  );
  return sortBy(uniqBy(channels, 'id'), 'name');
});
function getSvgName(channel: ReportSlackChannel) {
  switch (channel.channel_type) {
    case 'private-channel':
      return 'slackPrivate';
    case 'public-channel':
      return 'slackChannel';
    default:
      return 'slackShared';
  }
}

const selectableChannels = computed<SlackChannel[]>(() => {
  const query = channelSearchQuery.value.trim().toLowerCase();
  const alreadyListed = slackChannels.value.some(
    (channel) => channel.name.toLowerCase() === query,
  );
  if (!query || alreadyListed) return slackChannels.value;
  return [
    ...slackChannels.value,
    {
      id: '',
      channel_type: 'public-channel',
      name: query,
      label: `Use "#${query}"`,
      value: query,
      svg: 'slackChannel',
      isCustom: true,
    },
  ];
});

const selectedChannelName = ref<Nullable<string>>(null);
const selectedChannel = computed(() => {
  return slackChannels.value.find(
    (channel) => channel.name === selectedChannelName.value,
  );
});

const allPartnersInChannel = ref<Nullable<boolean>>(null);
const sharedChannelInfo = ref<Nullable<ReportSlackChannel>>(null);
const hasUnknownPartners = ref<Nullable<boolean>>(null);
const channelWasDeleted = ref<Nullable<boolean>>(null);

const isSelectedChannelShared = computed(
  () => selectedChannel.value?.channel_type === 'shared-channel',
);
const isPartnerDataOnly = computed(
  () => report.value?.consolidated_report_type === PARTNER_GREENFIELD,
);
const needsReauth = computed(() => slackApp.value?.needs_reauth);

async function fetchChannel(id: string): Promise<ReportSlackChannel | null> {
  try {
    const { data, error: apiError } = await crossbeamApi.GET(
      '/v0.1/slack-app/channels/{id}',
      { params: { path: { id } } },
    );
    if (apiError) captureException(apiError);
    return apiError || !data ? null : data;
  } catch (err) {
    captureException(err);
    return null;
  }
}

function getNotificationChannelId(
  notification: ReportNotification,
): string | undefined {
  if (
    'channel_id' in notification &&
    typeof notification.channel_id === 'string'
  ) {
    return notification.channel_id;
  }
  return notification.properties?.channel_id;
}

function pinChannel(channel: ReportSlackChannel) {
  if (!customLookupChannels.value.some((ch) => ch.id === channel.id)) {
    customLookupChannels.value = [...customLookupChannels.value, channel];
  }
}

function rehydrateSavedChannel(notification: ReportNotification) {
  const id = getNotificationChannelId(notification);
  if (!id) {
    selectedChannelName.value = '';
    return;
  }
  const savedChannel = {
    id,
    name:
      notification.properties?.channel_name ?? notification.slack_channel ?? id,
    channel_type: notification.properties?.channel_type ?? 'public-channel',
  };
  pinChannel(savedChannel);
  selectedChannelName.value = savedChannel.name;
}

async function rehydrate() {
  const slackNotification = modelValue.find(
    (notification) => notification.notification_type === 'slack',
  );
  if (!slackNotification) selectedChannelName.value = null;
  else if (isSlackUnavailable.value) rehydrateSavedChannel(slackNotification);
  else if (slackChannels.value) {
    const slackId = getNotificationChannelId(slackNotification);
    if (slackId || slackNotification.properties) {
      const match = slackChannels.value.find(
        (channel) => channel.id === slackId,
      );
      selectedChannelName.value = match?.name || '';
      if (match) pinChannel(match);
    } else if (slackNotification.slack_channel) {
      const match = slackChannels.value.find(
        (channel) => channel.name === slackNotification.slack_channel,
      );
      selectedChannelName.value = match?.name || '';
      if (match) pinChannel(match);
    }
    if (!selectedChannelName.value && slackId) {
      const verifiedChannel = await fetchChannel(slackId);
      if (verifiedChannel) {
        pinChannel(verifiedChannel);
        selectedChannelName.value = verifiedChannel.name;
      }
    }
    channelWasDeleted.value =
      !selectedChannelName.value && !isChannelListSyncing.value;
  }

  emails.value = modelValue
    .filter((notification) => notification.notification_type === 'email')
    .reduce((notifications: string[], notification: ReportNotification) => {
      if ('emails' in notification && notification.emails)
        return notifications.concat(notification.emails);
      return notifications;
    }, []);
}

async function loadSlack() {
  isLoadingSlack.value = true;
  try {
    await reportsStore.loadSlack();
  } finally {
    isLoadingSlack.value = false;
  }
}

async function rehydrateAndCheckChannel() {
  await rehydrate();
  if (isSelectedChannelShared.value && !isSlackUnavailable.value) {
    await checkChannelStatus(selectedChannel.value?.name || '');
  }
}

async function retryLoadSlack() {
  await loadSlack();
  await rehydrateAndCheckChannel();
}

onMounted(async () => {
  initiallyLoading.value = true;
  await Promise.all([
    partnersReadySync,
    loadSlack(),
    fromRoute && reportsStore.readySync,
  ]);
  await rehydrateAndCheckChannel();
  initiallyLoading.value = false;
});
async function checkChannelStatus(channel: string) {
  const customOption = selectableChannels.value.find(
    (option) => option.isCustom && option.value === channel,
  );
  resetSearch();
  if (customOption) {
    const lookedUpChannel = await lookUpCustomChannel(customOption.name);
    if (lookedUpChannel) await checkChannelStatus(lookedUpChannel.name);
    return;
  }
  if ((Array.isArray(channel) && !channel.length) || !channel) {
    selectedChannelName.value = null;
    return;
  }
  selectedChannelName.value = channel;
  if (selectedChannel.value) pinChannel(selectedChannel.value);
  if (
    selectedChannel.value &&
    selectedChannel.value.channel_type === 'shared-channel'
  ) {
    saving.value = true;
    const data = await fetchChannel(selectedChannel.value.id);
    if (!data) {
      sharedChannelInfo.value = null;
      hasUnknownPartners.value = null;
      allPartnersInChannel.value = null;
      flashesStore.addErrorFlash({
        message: `Couldn't verify #${selectedChannel.value.name}, please try again`,
      });
    } else {
      sharedChannelInfo.value = data;
      hasUnknownPartners.value = data.has_unknown_partners || null;
      allPartnersInChannel.value = reportPartnerIdList.value.every((id) =>
        data.known_partner_org_ids?.includes(id),
      );
    }
    saving.value = false;
  } else {
    hasUnknownPartners.value = null;
    allPartnersInChannel.value = null;
    sharedChannelInfo.value = null;
  }
}
const titleText = computed(() => {
  let result = 'Notification Settings';
  if (reportName) result += ` for ${reportName}`;
  return result;
});
const itemLabel = computed(() => {
  if (isPartnerDataOnly.value) return 'partner record';
  return 'overlap';
});

const showCantFindPartnerWarningText = computed(() => {
  return (
    selectedChannelName.value &&
    isSelectedChannelShared.value &&
    sharedChannelInfo.value &&
    !saving.value &&
    isPartnerDataOnly.value &&
    !allPartnersInChannel.value
  );
});
const getCantFindPartnerWarningText = {
  title: "We can't find your report partners in this Slack Connect channel",
  body: `Ensure your partners are in this Slack Connect channel and have the Crossbeam Slack integration installed for us to include their ${itemLabel.value} information in these notifications. Make sure this channel only includes people you want to have this partner record notification`,
};

const showSharedWithPartnersText = computed(() => {
  return (
    selectedChannelName.value &&
    isSelectedChannelShared.value &&
    sharedChannelInfo.value &&
    !saving.value &&
    !showCantFindPartnerWarningText.value
  );
});
const getSharedWithPartnersText = computed(() => {
  return {
    title: sharedChannelInfo.value?.known_partner_org_ids?.length
      ? `This report is sending notifications to a Slack Connect Channel with ${getOrgNames()}`
      : 'This report is sending notifications to a Slack Connect Channel',
    body: hasUnknownPartners.value
      ? `Note, the Crossbeam Slack integration hasn't been added by all organizations in this channel so we don't know who they are. Make sure this channel only includes people you want to have this ${itemLabel.value} information.`
      : '',
  };
});
function getOrgNames() {
  let orgNamesString = '';
  if (!sharedChannelInfo.value?.known_partner_org_ids) return orgNamesString;
  const orgNames = sharedChannelInfo.value.known_partner_org_ids
    .map((id) => getPartnerOrgById(id)?.name)
    .filter((name) => !!name);
  if (!orgNames) return orgNamesString;
  if (orgNames.length > 1) {
    const commaSeparatedOrgs = orgNames.slice(0, orgNames.length - 1);
    orgNamesString += commaSeparatedOrgs.join(', ');
    orgNamesString += ` and ${orgNames[orgNames.length - 1]}`;
  } else {
    orgNamesString = orgNames[0] as string;
  }
  return orgNamesString;
}

const deletedChannelDescription =
  "We don't have access to this channel anymore, Crossbeam was either removed or the channel was deleted.";
const reauthDescription =
  'This application has new and exciting features and needs to be updated to work properly. Visit the Integrations Page to update this.';

const toServer = computed(() => {
  const notifications = [];
  if (emails.value.length) {
    const serverEmails = emails.value;
    notifications.push({
      notification_type: 'email',
      emails: serverEmails,
    });
  }
  if (selectedChannelName.value) {
    notifications.push({
      notification_type: 'slack',
      channel_id: selectedChannel.value?.id || '',
    });
  }
  return notifications;
});
async function onSave() {
  v$.value.$touch();
  if (v$.value.$invalid) {
    await focusFirstInvalidField(form);
    return;
  }
  saving.value = true;
  const response = await reportsStore.saveNotificationConfigs({
    reportId: reportId.value,
    notifications: toServer.value,
  });
  saving.value = false;
  if (!response) {
    error.value = "Couldn't save notification settings, please try again";
    return;
  }
  emit('update:modelValue', response);
  hide();
  flashesStore.addSuccessFlash({ message: 'Notification Settings Saved' });
  await reportsStore.refreshReportsStore();
}

function hide() {
  error.value = null;
  emit('notifications-modal-hidden');
  rehydrate();
}

const nextUrl = computed(() => {
  const { name, params } = route;
  const url = router.resolve({
    name,
    params,
    query: {
      show_notification_configs: 'true',
    },
  }).href;
  return url;
});

watch([() => modelValue, () => report.value], () => {
  if (!initiallyLoading.value && !fromRoute) rehydrate();
});

defineExpose({
  showSharedWithPartnersText,
  getSharedWithPartnersText,
  showCantFindPartnerWarningText,
  getCantFindPartnerWarningText,
  toServer,
  rehydrate,
  emails,
  selectedChannelName,
  channelWasDeleted,
  areAllEmailsValid,
  error,
  isChannelListSyncing,
  showSyncingState,
  onChannelSearch,
  searchChannels,
  slackChannels,
});
</script>
<style lang="pcss" scoped>
@reference "../../css/theme.css";
.c-notification-settings {
  min-height: 100px;
}

.c-notification-settings__header {
  @apply flex items-center justify-between p-24;
}

.c-notification-settings__content {
  @apply flex flex-col py-24;
}

.c-notification-settings__footer {
  @apply flex p-24 pt-0 w-full justify-end;
}

.c-notification-settings__selector {
  @apply border-solid border-2 border-neutral-200 mb-24;
  border-radius: 4px !important;
}

.c-notification-settings__error {
  @apply text-brand-red pb-24;
  display: flex;
  justify-content: center;
  width: 100%;
  font-size: 0.75rem;
}

.c-notification-settings__email-invites {
  padding-bottom: 12px;
}

.c-notification-settings__loading {
  height: 500px;
}

.c-notification-settings__add-email {
  position: absolute;
  font-size: 1.5rem;
  margin-top: -42px;
  right: 0px;
  padding-right: 40px;
}

.c-notification-settings__text-input {
  font-size: 0.75rem;
}

.c-notification-settings__slack-button {
  width: 160px;
}

.c-notification-settings__slack-error {
  @apply flex items-center justify-between;
}
</style>
